import type { Container } from "pixi.js";
import type { PresentationTaskRunner, SymbolLandEffect } from "@slot/presentation";
import { BLANK } from "../rules/symbols";
import { COIN_LAND_POP } from "../tuning/holdTuning";
import { BLANK_LIT, OUT_OF_PLAY } from "./SymbolCatalog";

type SymbolLandAnimationRequest = Parameters<SymbolLandEffect["play"]>[0];

/** The empty cell's looks: its badge, blue or an active line's red (decision 56), and a cell out of play. None pops. */
const EMPTY_CELL_IDS: ReadonlySet<string> = new Set([BLANK, BLANK_LIT, OUT_OF_PLAY]);

/**
 * Every coin that lands pops (an empty cell's badge lands still, whatever its look), and is told to `onLanded` (its
 * glint). A new landing on a cell supersedes the running one; a new spin or a recovery puts every coin back at rest. The
 * player's slam (a tap, or a held Spin) settles a pop still running at once, and from it to the next roll every coin
 * lands with a quick, small pop and no glint (`COIN_LAND_POP.slam`; the user, 2026-10-06 and 2026-10-10).
 */
export class CoinLandEffect implements SymbolLandEffect {
  private readonly running = new Map<number, { node: Container; base: number; generation: number }>();
  private generation = 0;
  /** The roll in play was slammed: its coins land with the quick pop. */
  private slammed = false;

  public constructor(
    private readonly runner: PresentationTaskRunner,
    private readonly onLanded: (node: Container) => void = () => {},
  ) {}

  /** The player's slam: every pop settles now, and until the next roll starts (`finishAll`) a coin pops quickly. */
  public slam(): void {
    this.settleRunning();
    this.slammed = true;
  }

  public play(request: SymbolLandAnimationRequest): void {
    if (EMPTY_CELL_IDS.has(request.symbolId)) return;
    const node = request.cellNode;
    const pop = this.slammed ? COIN_LAND_POP.slam : COIN_LAND_POP.land;
    if (!this.slammed) this.onLanded(node);
    // A landing that supersedes one still running swells from that one's rest, not from its swollen scale.
    const previous = this.running.get(request.index);
    const base = previous && previous.node === node ? previous.base : node.scale.x;
    const generation = ++this.generation;
    this.running.set(request.index, { node, base, generation });
    void this.runner
      .tweenProgress(pop.seconds, (k) => {
        if (this.running.get(request.index)?.generation !== generation || node.destroyed) return;
        const s = base * (1 + pop.swell * Math.sin(Math.PI * k));
        node.scale.set(s, s);
      }, undefined, { easing: "linear" })
      .then(() => {
        if (this.running.get(request.index)?.generation !== generation) return;
        if (!node.destroyed) node.scale.set(base, base);
        this.running.delete(request.index);
      });
  }

  /** A new roll (or a recovery): every coin at rest, and the next landings pop again. */
  public finishAll(): void {
    this.settleRunning();
    this.slammed = false;
  }

  private settleRunning(): void {
    this.generation += 1;
    for (const { node, base } of this.running.values()) if (!node.destroyed) node.scale.set(base, base);
    this.running.clear();
  }
}
